import { useCallback, useEffect, useState } from 'react';
import type { ThemeMode } from './useTheme';

const THEME_STORAGE_KEY = 'amani-theme';

/**
 * Thème des pages publiques (vitrine, connexion) : le visiteur choisit jour ou sombre avec
 * l'interrupteur, son choix est mémorisé dans la même clé que l'espace connecté (il retrouve
 * donc le même thème après connexion). Sans choix mémorisé, la vitrine s'ouvre en clair —
 * « papier blanc, encre noire » de la maquette — là où l'app connectée démarre en sombre.
 */
export const usePublicTheme = () => {
    const [theme, setTheme] = useState<ThemeMode>(() => {
        try {
            const saved = localStorage.getItem(THEME_STORAGE_KEY);
            if (saved === 'light' || saved === 'dark') return saved;
        } catch { /* stockage indisponible : clair par défaut */ }
        return 'light';
    });

    useEffect(() => {
        const root = document.documentElement;
        root.setAttribute('data-theme', theme);
        root.classList.toggle('dark', theme === 'dark');
        root.classList.toggle('light', theme === 'light');
        try { localStorage.setItem(THEME_STORAGE_KEY, theme); } catch { /* ignore */ }
    }, [theme]);

    const toggleTheme = useCallback(() => setTheme((t) => (t === 'dark' ? 'light' : 'dark')), []);
    return { theme, isDark: theme === 'dark', toggleTheme };
};
